import React, { useCallback, useMemo, useState } from 'react';
import { Box, Tabs, Tab, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Chip } from '@mui/material';
import { Email, Sms, CheckCircle, Cancel, Visibility, Refresh as RefreshIcon, Edit as EditIcon, LocalOffer } from '@mui/icons-material';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import EmailBuilder from 'components/TemplateEditor/EmailTemplate/EmailBuilder/EmailBuilder';
import { SmsContentField } from 'components/TemplateEditor/GSMTextTemplate/SmsContentField';

export const Moderation: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'mail'|'sms'>('mail');

  const TABS: { label: string; value: 'mail' | 'sms'; icon: React.ReactElement; gradient: string; hoverGradient: string }[] = [
    { label: 'Email', value: 'mail', icon: <Email fontSize="small" />, gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', hoverGradient: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' },
    { label: 'SMS', value: 'sms', icon: <Sms fontSize="small" />, gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', hoverGradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' },
  ];

  const moderationSchema = useMemo(() => ({
    paths: {
      _id: { instance: 'String', isRequired: false },
      templateName: { instance: 'String', isRequired: true },
      companyName: { instance: 'String', isRequired: false },
      status: { instance: 'String', isRequired: false },
      updatedBy: { instance: 'String', isRequired: false },
      createdAt: { instance: 'Date', isRequired: false },
      updatedAt: { instance: 'Date', isRequired: false },
      actions: { instance: 'String', isRequired: false }
    },
    obj: {}
  }), []);

  const [refreshKey, setRefreshKey] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<any | null>(null);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<{ templateContent: string; templateId: string }>({ templateContent: '', templateId: '' });

  const getDisplayTemplateName = useCallback((fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    // Remove IUT-<companyId>- prefix if present (moderation shows cross-company)
    return fullTemplateName.replace(/^IUT-[^-]+-/, '');
  }, []);

  const handleApprove = useCallback(async (templateId: string) => {
    try {
      await apiClient.put(`/iu/contenttemplate/${templateId}/approve`);
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      Logger.error('Moderation: approve failed', e);
    }
  }, []);

  const handleReject = useCallback(async (templateId: string) => {
    try {
      await apiClient.put(`/iu/contenttemplate/${templateId}/reject`);
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      Logger.error('Moderation: reject failed', e);
    }
  }, []);

  const handleView = useCallback((record: any) => {
    setViewRecord(record);
    setEmailPreviewHtml('');
    setViewOpen(true);
  }, []);

  const handleEditOpen = useCallback((record: any) => {
    setEditRecord(record);
    setEditForm({
      templateContent: String(record?.templateContent ?? ''),
      templateId: String(record?.templateId ?? '')
    });
    setEditOpen(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditOpen(false);
    setEditRecord(null);
    setEditForm({ templateContent: '', templateId: '' });
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editRecord?._id) return;
    try {
      await apiClient.put(`/iu/contenttemplate/${editRecord._id}`, {
        templateContent: editForm.templateContent,
        templateId: (editForm.templateId || '').trim() || null
      });
      setRefreshKey(prev => prev + 1);
      handleEditClose();
    } catch (e) {
      Logger.error('Moderation: edit save failed', e);
    }
  }, [editRecord, editForm, handleEditClose]);

  const activeTabConfig = TABS.find(t => t.value === activeTab);

  return (
    <Box p={2}>
      <Typography variant="h6" sx={{ mb: 2 }}>Moderation</Typography>
      <Box sx={{ mb: 0, px: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 0,
            p: 0,
            m: 0,
            '& .MuiTabs-scroller': { p: 0, m: 0 },
            '& .MuiTabs-flexContainer': { gap: 0, p: 0, m: 0 },
            '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
            '& .MuiTabs-indicator': { backgroundColor: 'secondary.main' }
          }}
        >
          {TABS.map(tab => (
            <Tab
              key={tab.value}
              value={tab.value}
              iconPosition="start"
              icon={tab.icon}
              label={tab.label}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mr: 0.5,
                px: 1.5,
                minHeight: 40,
                color: activeTab === tab.value ? 'common.white' : 'text.primary',
                background: activeTab === tab.value ? tab.gradient : 'transparent',
                '&.Mui-selected': {
                  color: 'common.white'
                },
                '&:hover': {
                  background: activeTab === tab.value ? tab.hoverGradient : 'action.hover'
                },
                '&:first-of-type': { ml: 0 }
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        backgroundColor: 'background.paper',
        mb: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeTabConfig?.icon}
            {activeTabConfig?.label} Templates — Pending Approval
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setRefreshKey(prev => prev + 1)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
        </Box>
        <DataTableWithStaticSchema
          dataUrl={`/iu/contenttemplate/moderation/${activeTab}`}
          schemaObject={moderationSchema}
          columnsToExclude={['_id', ...(activeTab === 'mail' ? ['status'] : [])]}
          columnsWithDisabledFilter={['status','updatedBy','actions']}
          refreshTrigger={refreshKey}
          columnRenderers={{
            templateName: (row: any) => getDisplayTemplateName(row.templateName),
            companyName: (row: any) => row.companyName || '',
            status: (row: any) => {
              const s = row.status;
              if (!s || activeTab !== 'sms') return null;
              if (s === 'VERIFIED') return <Chip label="VERIFIED" size="small" color="success" sx={{ fontWeight: 600 }} />;
              if (s === 'MODIFIED') return <Chip label="MODIFIED" size="small" sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', fontWeight: 600 }} />;
              if (s === 'NEW') return <Chip label="NEW" size="small" color="error" sx={{ fontWeight: 600 }} />;
              return null;
            },
            updatedBy: (row: any) => row.updatedByEmail || '',
            actions: (row: any) => {
              const status = row.status;
              const isSms = activeTab === 'sms';
              /** When company IU plan gsmWorkerName is not TATA-TELE-SMS-WORKER, API allows approve without system-template body match. */
              const skipSystemMatch = row.smsModerationSkipSystemTemplateMatch === true;
              const canApprove = !isSms || skipSystemMatch || status === 'VERIFIED';
              const showEdit = isSms && (status === 'NEW' || status === 'MODIFIED');
              return (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={canApprove ? 'Approve' : 'Approve only when template body matches the IU system template (TATA-TELE-SMS-WORKER)'}>
                    <span>
                      <Button
                        aria-label="approve"
                        color="success"
                        variant="contained"
                        size="small"
                        disabled={!canApprove}
                        onClick={() => handleApprove(row._id)}
                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                      >
                        <CheckCircle fontSize="small" />
                      </Button>
                    </span>
                  </Tooltip>
                  {showEdit && (
                    <Tooltip title="Edit">
                      <Button
                        aria-label="edit"
                        color="secondary"
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditOpen(row)}
                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                      >
                        <EditIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Reject">
                    <Button
                      aria-label="reject"
                      color="error"
                      variant="contained"
                      size="small"
                      onClick={() => handleReject(row._id)}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                    >
                      <Cancel fontSize="small" />
                    </Button>
                  </Tooltip>
                  <Tooltip title="View">
                    <Button
                      aria-label="view"
                      color="secondary"
                      variant="contained"
                      size="small"
                      onClick={() => handleView(row)}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                    >
                      <Visibility fontSize="small" />
                    </Button>
                  </Tooltip>
                </Box>
              );
            }
          }}
        />
      </Box>
      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Details</DialogTitle>
        <DialogContent dividers>
          {viewRecord && (
            <Grid container spacing={2}>
              {(() => {
                const orderedKeys = [
                  'templateName', 'templateType',
                  'isApproved', 'approvedBy', 'approvedAt',
                  'createdAt', 'updatedAt', 'collectionId',
                  'templateUpdatedOn', 'status', 'emailSubject'
                ];
                const keysSet = new Set(Object.keys(viewRecord || {}));
                const keys: string[] = [];
                // Exclude heavy/system fields; we'll handle templateContent separately at the end
                const excluded = new Set<string>([
                  'companyId', 'accessLevel', 'isApprovalRequired', 'templateCreatedByUserId', 'templateUpdatedByUserId',
                  '_id', '__v', '_v', 'sharedTo', 'mediaAttachments', 'emailBuilderConfig', 'templateContent',
                  'thumbnail', 'thumbnailUrl', 'estimatedCreditCost'
                ]);
                const showTemplateContent = activeTab === 'sms';
                orderedKeys.forEach(k => { if (keysSet.has(k)) keys.push(k); });
                Object.keys(viewRecord || {}).forEach(k => { if (!keys.includes(k) && !excluded.has(k)) keys.push(k); });

                const formatValue = (key: string, value: any): string => {
                  if (value === null || value === undefined) return '';
                  const dateLike = ['createdAt','updatedAt','approvedAt','templateUpdatedOn'];
                  if (dateLike.includes(key) && value) {
                    try { return new Date(value).toLocaleString('en-GB'); } catch { /* noop */ }
                  }
                  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
                  if (typeof value === 'object') return JSON.stringify(value, null, 2);
                  return String(value);
                };

                return (
                  <>
                    {/* Email preview (for Email templates only) */}
                    {String((viewRecord as any)?.templateType || '').toLowerCase().includes('mail') && (viewRecord as any)?.emailBuilderConfig && (
                      <Grid item xs={12}>
                        {/* Hidden builder to export HTML */}
                        <Box sx={{ display: 'none' }}>
                          <EmailBuilder value={(viewRecord as any).emailBuilderConfig} onExportHtml={(html: string) => setEmailPreviewHtml(html)} />
                        </Box>
                        {emailPreviewHtml && (
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <iframe title="email-preview" style={{ width: '100%', height: 480, border: 'none' }} srcDoc={emailPreviewHtml} />
                          </Box>
                        )}
                      </Grid>
                    )}
                    {keys.map((key) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <TextField
                          label={key}
                          value={formatValue(key, (viewRecord as any)[key])}
                          fullWidth
                          variant="outlined"
                          size="small"
                          InputProps={{ readOnly: true }}
                          multiline={typeof (viewRecord as any)[key] === 'object'}
                          minRows={typeof (viewRecord as any)[key] === 'object' ? 2 : undefined}
                        />
                      </Grid>
                    ))}
                    {showTemplateContent && typeof (viewRecord as any)?.estimatedCreditCost === 'number' && (
                      <Grid item xs={12}>
                        <Chip
                          icon={<LocalOffer sx={{ fontSize: 18 }} />}
                          label={String((viewRecord as any).estimatedCreditCost)}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            color: 'success.main',
                            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.96)'),
                            border: '1px solid',
                            borderColor: 'success.main',
                            height: 28,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                            '& .MuiChip-icon': { ml: 0.5, color: 'success.main' },
                            '& .MuiChip-label': { px: 0.75, fontSize: '0.8rem' },
                          }}
                        />
                      </Grid>
                    )}
                    {/* Place templateContent at the bottom for SMS tab — same UX as Instant Updates SMS editor */}
                    {showTemplateContent && typeof (viewRecord as any)?.templateContent !== 'undefined' && (
                      <Grid item xs={12}>
                        <SmsContentField
                          dialogOpen={viewOpen}
                          contentKey={(viewRecord as any)?._id}
                          value={String((viewRecord as any).templateContent ?? '')}
                          onChange={() => {}}
                          readOnly
                        />
                      </Grid>
                    )}
                  </>
                );
              })()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog (SMS templates - templateContent & templateId only) */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit SMS Template</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Template ID"
                value={editForm.templateId}
                onChange={(e) => setEditForm(f => ({ ...f, templateId: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="DLT Template ID (optional)"
              />
            </Grid>
            <Grid item xs={12}>
              <SmsContentField
                dialogOpen={editOpen}
                contentKey={editRecord?._id}
                value={editForm.templateContent}
                onChange={(plain) => setEditForm((f) => ({ ...f, templateContent: plain }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Moderation;


